import React, { useState, useMemo } from "react";
import { mimeLabel, getTagLabel } from "../../utils/algoliaFormatters";
import { tagMapToIndex, getAncestorPathLabel, getFullPathLabel } from "../../utils/tagHierarchy";
import { FacetSectionHeader } from "./FacetSectionHeader";

const COLLAPSED_COUNT = 6;   // values shown before "+N more"
const SEARCH_THRESHOLD = 8;  // show the in-facet search box past this many values

export function FacetSection({ title, facetKey, facetData = {}, selected = [], onToggle, tagMap = {}, labelMap = null, defaultExpanded = false, children }) {
    // Every section used to open expanded, which turned the sidebar into one
    // long scroll of seven stacked lists. The two people reach for first stay
    // open; the rest announce themselves with a count badge when they're
    // filtering, and a section arriving already-filtered (a seeded URL) opens so
    // its selection is never hidden behind a collapsed header.
    const [expanded, setExpanded] = useState(defaultExpanded || selected.length > 0);
    const [showAll,  setShowAll]  = useState(false);
    const [filter,   setFilter]   = useState("");

    const isTags = facetKey === "metadata.tags";
    const tagIndex = useMemo(() => (isTags ? tagMapToIndex(tagMap) : null), [isTags, tagMap]);

    // Human-readable label for a raw facet value (used for both display + search).
    // `labelMap` covers any facet whose buckets are IDS rather than words —
    // projects, since the D1 cutover moved the facet from free-text names onto
    // real Project ids. Generic rather than another `facetKey ===` branch,
    // because the next id-shaped facet shouldn't need this file edited again.
    const labelFor = (value) =>
        isTags ? getTagLabel(value, tagMap)
        : facetKey === "metadata.mimetype" ? mimeLabel(value)
        : labelMap ? (labelMap[value] ?? value)
        : value;

    const entries = useMemo(
        () => Object.entries(facetData).sort((a, b) => b[1] - a[1]),
        [facetData]
    );

    const term = filter.trim().toLowerCase();
    const filtered = useMemo(
        () => (term ? entries.filter(([value]) => labelFor(value).toLowerCase().includes(term)) : entries),
        // labelFor closes over tagMap/facetKey which are stable for a given section
        [entries, term] // eslint-disable-line react-hooks/exhaustive-deps
    );

    if (entries.length === 0) return null;

    const showSearch = entries.length > SEARCH_THRESHOLD;
    // While searching, surface every match (no truncation); otherwise honour show-all.
    const visible = term ? filtered : (showAll ? filtered : filtered.slice(0, COLLAPSED_COUNT));
    const hiddenCount = filtered.length - COLLAPSED_COUNT;

    return (
        <div className="asp-facet-section">
            <FacetSectionHeader
                title={title}
                expanded={expanded}
                onToggle={() => setExpanded(e => !e)}
                count={selected.length}
            />

            {expanded && (
                <div className="asp-facet-body">
                    {children}

                    {showSearch && (
                        <div className="asp-facet-search">
                            <input
                                type="text"
                                value={filter}
                                placeholder={`Filter ${title.toLowerCase()}…`}
                                onChange={(e) => setFilter(e.target.value)}
                                aria-label={`Filter ${title}`}
                            />
                            {filter && (
                                <button
                                    type="button"
                                    className="asp-facet-search-clear"
                                    onClick={() => setFilter("")}
                                    aria-label="Clear filter"
                                >×</button>
                            )}
                        </div>
                    )}

                    {visible.map(([value, count]) => {
                        const isChecked = selected.includes(value);
                        const tagPath = isTags ? getAncestorPathLabel(value, tagIndex) : "";
                        return (
                            <label key={value} className={`asp-facet-item ${isChecked ? "checked" : ""}`}>
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => onToggle(facetKey, value)}
                                />
                                {isTags ? (
                                    <span
                                        className="asp-facet-label asp-facet-label--tag"
                                        title={getFullPathLabel(value, tagIndex)}
                                    >
                                        <span className="asp-facet-tag-name">
                                            <span
                                                className="asp-facet-dot"
                                                style={{ background: tagMap[value]?.color || "var(--qd-text-subtle)" }}
                                                aria-hidden="true"
                                            />
                                            {getTagLabel(value, tagMap)}
                                        </span>
                                        {tagPath && <span className="asp-facet-tag-path">{tagPath}</span>}
                                    </span>
                                ) : (
                                    <span className="asp-facet-label" title={labelFor(value)}>
                                        {labelFor(value)}
                                    </span>
                                )}
                                <span className="asp-facet-count">{count.toLocaleString()}</span>
                            </label>
                        );
                    })}

                    {term && filtered.length === 0 && (
                        <p className="asp-facet-empty">No matches</p>
                    )}

                    {!term && hiddenCount > 0 && (
                        <button
                            type="button"
                            className="asp-show-more"
                            onClick={() => setShowAll(s => !s)}
                        >
                            {showAll ? "Show less" : `+${hiddenCount} more`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
